import { useNavigate } from 'react-router-dom';
import { Button, useToast } from '../../../components/ui';
import { customerFilter, dateFilter, workerFilter } from '../../../components/common/ListPage/listFilters';
import { Cell, ListPage } from '../../../components/common/ListPage/ListPage';
import { StatusBadge } from '../../../components/common/Parts/Parts';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useAction } from '../../../hooks/useAction';
import { JOB_STATUS, PRIORITY } from '../../../constants/status';
import { labelOf } from '../../../utils/labels';
import { masterLabel } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { formatDate } from '../../../utils/date';
import { jobService } from '../services/jobService';

const TABS = [
  { value: '', label: 'All' },
  { value: 'pending,assigned,in_progress,on_hold', label: 'Open' },
  ...Object.entries(JOB_STATUS).map(([value, [label]]) => ({ value, label })),
];
const NEXT = { assigned: ['in_progress', 'Start work', 'wrench'], on_hold: ['in_progress', 'Resume work', 'wrench'], in_progress: ['completed', 'Mark completed', 'checkCircle'] };

export default function JobList() {
  const navigate = useNavigate();
  const toast = useToast();
  const { run } = useAction(toast);
  const { isStaff } = useAuth();
  useMasters();
  const add = isStaff && <Button leftIcon="plus" onClick={() => navigate('/jobs/new')}>New service job</Button>;

  const rowActions = (s, { reload }) => {
    const next = NEXT[s.status];
    const open = !['invoiced', 'cancelled'].includes(s.status);
    return [
      { label: 'View', icon: 'eye', onClick: () => navigate(`/jobs/${s._id}`) },
      isStaff && open && { label: 'Edit', icon: 'edit', onClick: () => navigate(`/jobs/${s._id}/edit`) },
      next && { label: next[1], icon: next[2], onClick: async () => (await run('status', () => jobService.setStatus(s._id, next[0]), `${s.serviceNo}: ${labelOf(JOB_STATUS, next[0])}`)) && reload() },
      isStaff && s.status === 'completed' && !s.invoiceId && {
        label: 'Create invoice',
        icon: 'receipt',
        onClick: async () => {
          const inv = await run('inv', () => jobService.createInvoice(s._id, 'gst'), 'Draft invoice created');
          if (inv && inv !== true) navigate(`/invoices/${inv._id}`);
        },
      },
      isStaff && s.invoiceId && { label: 'Open invoice', icon: 'receipt', onClick: () => navigate(`/invoices/${s.invoiceId}`) },
    ].filter(Boolean);
  };

  return (
    <ListPage
      title={isStaff ? 'Service jobs' : 'My jobs'}
      path="/services"
      tabs={TABS}
      filters={[
        dateFilter('Visit date'),
        ...(isStaff ? [customerFilter, workerFilter] : []),
      ]}
      searchPlaceholder="Job no, customer, title"
      actions={
        <>
          <Button variant="outline" leftIcon="columns" onClick={() => navigate('/jobs/board')}>Board view</Button>
          {add}
        </>
      }
      rowTo={(s) => `/jobs/${s._id}`}
      rowActions={rowActions}
      columns={[
        { header: 'Job no.', sort: 'serviceNo', sortDesc: true, render: (s) => s.serviceNo, csv: (s) => s.serviceNo },
        { header: 'Customer / job', sort: 'customer.name', render: (s) => <Cell title={s.customer?.name} sub={s.title} />, csv: (s) => s.customer?.name },
        { header: 'Status', sort: 'status', render: (s) => <StatusBadge map={JOB_STATUS} value={s.status} />, csv: (s) => labelOf(JOB_STATUS, s.status) },
        { header: 'Priority', sort: 'priority', render: (s) => (s.priority && s.priority !== 'normal' ? <StatusBadge map={PRIORITY} value={s.priority} /> : <span className="text-ink-400">Normal</span>), csv: (s) => s.priority },
        { header: 'Work type', sort: 'serviceType', render: (s) => masterLabel('serviceType', s.serviceType), csv: (s) => masterLabel('serviceType', s.serviceType), defaultHidden: true },
        { header: 'Visit date', sort: 'scheduledDate', sortDesc: true, render: (s) => (s.scheduledDate ? formatDate(s.scheduledDate) : <span className="text-ink-300">Not set</span>), csv: (s) => formatDate(s.scheduledDate) },
        { header: 'Workers', render: (s) => (s.assignedTo?.length ? s.assignedTo.map((u) => u.name).join(', ') : <span className="text-ink-300">-</span>), csv: (s) => s.assignedTo?.map((u) => u.name).join(', ') },
      ]}
      mobile={(s) => ({
        title: s.customer?.name,
        subtitle: [s.serviceNo, s.title, s.scheduledDate && formatDate(s.scheduledDate)].filter(Boolean).join(' · '),
        badge: <StatusBadge map={JOB_STATUS} value={s.status} />,
      })}
      empty={{
        icon: 'wrench',
        title: isStaff ? 'No service jobs yet' : 'No jobs for you right now',
        message: isStaff ? 'Create a job directly, or from an accepted quotation.' : 'When the office gives you a job, it will show here.',
        action: add,
      }}
    />
  );
}
